import { useClerk } from "@clerk/nextjs";
import { type UseQueryOptions, type UseQueryResult, useQuery } from "@tanstack/react-query";

import { AuthContext } from "@/data/auth/auth.context";
import type { ApplicationException, GeneralErrorCodes } from "@/util/vendor/error-handling";

/**
 * A wrapper around useQuery that automatically disables the query when no auth token is available.
 * This prevents authenticated endpoints from being called without proper authentication.
 *
 * @param options - Standard TanStack Query options (enabled will be overridden)
 * @returns UseQueryResult with auth guard protection
 */
export function useAuthGuardedQuery<
  TQueryFnData = unknown,
  TError = ApplicationException<GeneralErrorCodes>,
  TData = TQueryFnData,
  TQueryKey extends readonly unknown[] = readonly unknown[],
>(options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>): UseQueryResult<TData, TError> {
  const { isLoggedIn, isInitializing, isClerkTokenApplied } = AuthContext.useAuth();
  const { session } = useClerk();

  // Determine if the query should be enabled
  // We need to ensure the auth interceptor is attached before enabling queries
  const shouldEnableQuery = Boolean(!isInitializing && isLoggedIn && session && isClerkTokenApplied);

  return useQuery({
    ...options,
    enabled: shouldEnableQuery && options?.enabled !== false,
  });
}

/**
 * Hook that returns whether authenticated queries should be enabled.
 * Useful for conditional query enabling in more complex scenarios.
 *
 * @returns boolean indicating if auth-protected queries should be enabled
 */
export function useAuthQueryEnabled(): boolean {
  const { isLoggedIn, isInitializing, isClerkTokenApplied } = AuthContext.useAuth();
  const { session } = useClerk();

  return Boolean(!isInitializing && isLoggedIn && session && isClerkTokenApplied);
}
